import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { Formik, FormikErrors, FormikProps } from "formik";
import { useRecoilState } from "recoil";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PortalStatusBanner, PortalStatusFlag } from "@bto/shared";
import { Button } from "../shared/Button/Button";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  ButtonContainer,
  CenterContainer,
  DesktopBrandLockup,
  DesktopBrandPanel,
  DesktopBrandRow,
  DesktopBrandTitleAside,
  DesktopBrandTitleImg,
  DesktopColorBackground,
  DesktopContainer,
  DesktopLoginGradientLayer,
  FormFieldInput,
  LoginErrorWrapper,
  LoginFieldsSection,
  LoginFormColumn,
  LoginHeaderBlock,
  LoginHeroBand,
  LoginHeroLockupImg,
  LoginHeroTitleImg,
  LoginInput,
  LoginPassword,
  LoginPrivacyPolicy,
  NonDesktopContents,
  NonDesktopShell,
  PasswordLinkContainer,
  StatusBannerWrapper,
} from "./Login.styles";
import providerPortalLockup from "../../assets/logos/provider-portal/unity-vert-cobranded.svg";
import providerPortalLockupHorizontal from "../../assets/logos/provider-portal/unity-horiz-cobranded.svg";
import providerPortalTitle from "../../assets/logos/provider-portal/provider-portal-title.svg";
import LoginRequest, {
  LoginInitialValues,
  LoginRequestType,
} from "./Services/LoginService";
import {
  LANDING_PAGE_URL,
  RESET_PASSWORD_URL,
  TERMS_OF_USE_URL,
  UNIVERSAL_LOGIN_PAGE_URL,
} from "../../utils/urlConstants";
import { MFA_REQUIRED_ERROR_CODE } from "../../utils/authErrorCodes";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  defaultReqsRequestComplete,
  defaultRequisitions,
  providerProfileState,
  providerState,
} from "../../recoil/atoms";
import { UserAuthProviderResponse } from "../../recoil/types";
import { validateEmail } from "../../utils/hooks/helpers";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { RefreshRequisitionRows } from "../Grid/Services/GridService";
import { RefreshProviderProfile } from "../Profile/Services/UpdateProfileInfoService";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import {
  setCookieAccessToken,
  removeCookieAccessToken,
} from "../../utils/requestUtils";

type LoginError = "invalidCredentials" | "mfaRequired" | "network" | null;

const getLoginErrorContent = (
  loginError: LoginError,
): React.ReactNode | null => {
  switch (loginError) {
    case "mfaRequired":
      return (
        <>
          Your account has two-factor authentication enabled. Please sign in at
          the <Link to={UNIVERSAL_LOGIN_PAGE_URL}>Login Page</Link>.
        </>
      );
    case "network":
      return "Please check your network connection.";
    case "invalidCredentials":
      return "Invalid email and password combination. Please try again.";
    default:
      return null;
  }
};

export const LoginForm = (): React.ReactElement => {
  const history = useHistory();
  const { isDesktop, deviceType } = useDeviceHook();
  const flags = useFlags();
  const portalStatus = flags[FeatureFlags.PortalStatus] as
    | PortalStatusFlag
    | undefined;
  const [loginError, setLoginError] = useState<LoginError>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [providerAuth, setProviderAuth] = useRecoilState(providerState);
  const [, setProviderProfile] = useRecoilState(providerProfileState);
  const [, setRequisitions] = useRecoilState(defaultRequisitions);
  const [, setDefaultReqsComplete] = useRecoilState(defaultReqsRequestComplete);
  const { setLoggedOut, loggedOutInactivityFlag, setLoggedOutInactivityFlag } =
    useAuthenticationHook();
  const loginErrorContent = getLoginErrorContent(loginError);

  useEffect(() => {
    if (providerAuth?.token) {
      history.push(
        sessionStorage.getItem("current-pathname") || LANDING_PAGE_URL,
      );
      sessionStorage.removeItem("current-pathname");
    }
  }, [history, providerAuth?.token]);

  const onSubmit = async ({
    email,
    password,
  }: LoginRequestType): Promise<void> => {
    setLoginError(null);
    setIsLoading(true);
    try {
      const response = await LoginRequest({
        email,
        password,
      });
      if (loggedOutInactivityFlag) {
        setLoggedOutInactivityFlag(false);
      }
      if (response.ok) {
        const userData: UserAuthProviderResponse = await response.json();
        setCookieAccessToken(userData.token);
        await RefreshProviderProfile(
          userData.token,
          setProviderProfile,
          setLoggedOut,
        );
        setProviderAuth(userData);
        await RefreshRequisitionRows(
          userData.token,
          setRequisitions,
          setLoggedOut,
          undefined,
          setDefaultReqsComplete,
        );
      } else {
        removeCookieAccessToken();
        setProviderAuth(undefined);
        const body = await response.json().catch(() => null);
        const isMfaRequired =
          response.status === 403 && body?.error === MFA_REQUIRED_ERROR_CODE;
        setLoginError(isMfaRequired ? "mfaRequired" : "invalidCredentials");
      }
    } catch {
      setLoginError("network");
    } finally {
      setIsLoading(false);
    }
  };

  const loginSection = (
    <CenterContainer>
      <LoginFormColumn>
        <LoginHeaderBlock>
          {GetTypographyContent({
            content: "Welcome to Unity™",
            size: "xlarge",
            textColor: "neutral700",
          })}
          {GetTypographyContent({
            content: "Log into your account.",
            size: "h3",
            isBold: true,
            textColor: "greyMedium",
          })}
          {GetTypographyContent({
            content: "Please enter your credentials below",
            size: "large",
            textColor: "greyMedium",
          })}
        </LoginHeaderBlock>
        {portalStatus?.enabled && (
          <StatusBannerWrapper deviceType={deviceType}>
            <PortalStatusBanner
              status={portalStatus.status}
              title={portalStatus.title}
              message={portalStatus.message}
              statusPageUrl={portalStatus.statusPageUrl}
            />
          </StatusBannerWrapper>
        )}
        {loginErrorContent && (
          <LoginErrorWrapper deviceType={deviceType} role="alert">
            {GetTypographyContent({
              content: loginErrorContent,
              size: "medium",
              textColor: "redDark",
            })}
          </LoginErrorWrapper>
        )}
        {loggedOutInactivityFlag && !loginErrorContent && (
          <LoginErrorWrapper deviceType={deviceType} role="alert">
            {GetTypographyContent({
              content: "Logged out for security. Please login again.",
              size: "medium",
              textColor: "redDark",
            })}
          </LoginErrorWrapper>
        )}
        <Formik
          initialValues={LoginInitialValues}
          validateOnChange={false}
          validate={(values) => {
            const errors: FormikErrors<LoginRequestType> = {};
            if (!values.email) {
              errors.email = "Required";
            }
            if (!values.password) {
              errors.password = "Required";
            }
            if (!validateEmail(values.email)) {
              errors.email = "Invalid email";
            }
            return errors;
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await onSubmit(values);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            setFieldError,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }: FormikProps<LoginRequestType>) => (
            <>
              <LoginFieldsSection deviceType={deviceType}>
                <FormFieldInput deviceType={deviceType}>
                  <LoginInput
                    type="text"
                    name="email"
                    value={values.email}
                    label="Email"
                    required
                    size="small"
                    onChange={(e) => {
                      setLoginError(null);
                      setFieldError("email", "");
                      setFieldValue("email", e.target.value);
                    }}
                    error={errors.email}
                  />
                </FormFieldInput>
                <FormFieldInput deviceType={deviceType}>
                  <LoginPassword
                    type="password"
                    name="password"
                    value={values.password}
                    label="Password"
                    required
                    size="small"
                    onChange={(e) => {
                      setLoginError(null);
                      setFieldError("password", "");
                      setFieldValue("password", e.target.value);
                    }}
                    error={Boolean(errors.password)}
                    helperText={errors.password}
                  />
                </FormFieldInput>
                <PasswordLinkContainer deviceType={deviceType}>
                  {GetTypographyContent({
                    content: "Forgot password?",
                    size: "small",
                    textColor: "greenDark",
                    link: RESET_PASSWORD_URL,
                  })}
                </PasswordLinkContainer>
              </LoginFieldsSection>
              <LoginPrivacyPolicy deviceType={deviceType}>
                <p>
                  {`By continuing, you agree to our `}
                  <a
                    href="https://www.billiontoone.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                  {`, `}
                  <a
                    href="https://www.billiontoone.com/privacy-practices"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Notice of Privacy Practices
                  </a>
                  {`, and `}
                  <a href={TERMS_OF_USE_URL} target="_blank" rel="noreferrer">
                    Terms of Use
                  </a>
                  .
                </p>
              </LoginPrivacyPolicy>
              {isLoading ? (
                <>
                  <SpinnerLoading />
                </>
              ) : (
                <ButtonContainer>
                  <Button
                    type="submit"
                    label="Login"
                    id="LoginButton"
                    size="medium"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    textColor="white"
                  />
                </ButtonContainer>
              )}
            </>
          )}
        </Formik>
      </LoginFormColumn>
    </CenterContainer>
  );

  return (
    <>
      {!providerAuth?.token && isDesktop && (
        <DesktopContainer>
          <DesktopColorBackground>
            <DesktopLoginGradientLayer aria-hidden />
            <DesktopBrandPanel>
              <DesktopBrandRow>
                <DesktopBrandLockup
                  src={providerPortalLockup}
                  alt="Unity by BillionToOne"
                />
                <DesktopBrandTitleAside>
                  <DesktopBrandTitleImg
                    src={providerPortalTitle}
                    alt="Provider Portal"
                  />
                </DesktopBrandTitleAside>
              </DesktopBrandRow>
            </DesktopBrandPanel>
          </DesktopColorBackground>
          {loginSection}
        </DesktopContainer>
      )}
      {!providerAuth?.token && !isDesktop && (
        <NonDesktopShell>
          <LoginHeroBand $deviceType={deviceType}>
            <LoginHeroLockupImg
              $deviceType={deviceType}
              src={providerPortalLockupHorizontal}
              alt="Unity by BillionToOne"
            />
            <LoginHeroTitleImg
              $deviceType={deviceType}
              src={providerPortalTitle}
              alt="Provider Portal"
            />
          </LoginHeroBand>
          <NonDesktopContents $deviceType={deviceType}>
            {loginSection}
          </NonDesktopContents>
        </NonDesktopShell>
      )}
    </>
  );
};
